import { Check, Loader2, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import type { Title } from '@shared/types';

import { CinematicHero } from '@/components/CinematicHero';
import { PerspectiveCover } from '@/components/PerspectiveCover';
import { Poster, POSTER_SIZES } from '@/components/Poster';
import { RailTrack } from '@/components/RailTrack';
import { Reveal, RevealFill } from '@/components/Reveal';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { showKey, useSeason } from '@/features/show/api';
import type { UpNextItem } from '@/features/upnext/model';
import { NewChip, UpNextProgress } from '@/features/upnext/UpNextCard';
import { useUpNextEntry } from '@/features/upnext/useUpNextEntry';
import { localized } from '@/i18n/localized';
import { useLocale } from '@/i18n/locale';
import { useAmbient } from '@/lib/ambient';
import { useMediaQuery } from '@/lib/use-media-query';

/*
 * Tonight's "now showing" (docs/screens.md §`/`): the first Up next title —
 * the view's single next action (amber Continue) — then the rest of Up next
 * as a rail of perspective cases with a neutral quick mark.
 *
 * From `sm` ("Shelf"): no backdrop band — the quiet canvas carries the
 * title's colour through the ambient wash only; the poster is the hero
 * object, a large case resting turned (`PerspectiveCover variant="rest"`),
 * with the copy to its right. Below `sm`: a 16:9 backdrop, the copy under it.
 *
 * Load settle (ui-ux.md §4; the caller wraps this in a `RevealGroup`): the
 * case swings in (order 1), the copy follows line by line (4–8), the
 * progress fill grows (9); the rail's group starts at 9.
 */

/** The row's height from `sm`; the skeleton reserves the same. */
const HERO_ROW = 'sm:min-h-[clamp(320px,46vh,480px)] sm:gap-10 lg:gap-16';
/** The hero case's width from `sm`. */
const HERO_CASE = 'w-[200px] shrink-0 md:w-[220px] lg:w-[260px]';

/** TMDB's placeholder names ("Episode 7") add nothing next to "Episode 7". */
const GENERIC_EPISODE_NAME = /^epis[oó]d(e|io)\s*\d+$/i;

export function NowShowing({ item }: { item: UpNextItem }) {
  const { locale, t } = useLocale();
  const qc = useQueryClient();
  const entry = useUpNextEntry(item);
  const { id, name, next, progress } = entry;
  // Tonight is led by the Now Showing title: its palette tints the canvas.
  useAmbient(item.title.palette);
  // The backdrop is phone-only: don't fetch it where the case leads.
  const wide = useMediaQuery('(min-width: 640px)');

  // Home rows carry the backdrop; a cached detail page is the free fallback.
  const cached = item.title.backdropUrl ? null : qc.getQueryData<Title | null>(showKey(id));
  const backdropUrl = item.title.backdropUrl ?? cached?.backdropUrl;
  const backdropFallbackUrl = item.title.backdropUrl
    ? item.title.backdropFallbackUrl
    : cached?.backdropFallbackUrl;

  // The next episode's name: one season-detail read for the hero only (the
  // same cache entry Continue reads, so the click then needs no fetch).
  const season = useSeason(id, next?.season ?? 0, next !== null);
  const episode = next ? season.data?.episodes.find((e) => e.episodeNumber === next.episode) : undefined;
  const episodeName = episode ? localized(episode.title, episode.titleBr, locale) : '';
  const showEpisodeName = episodeName !== '' && !GENERIC_EPISODE_NAME.test(episodeName.trim());

  return (
    <article aria-label={name}>
      {wide ? null : (
        <Reveal>
          <CinematicHero
            backdropUrl={backdropUrl}
            fallbackUrl={backdropFallbackUrl}
            palette={item.title.palette}
            title={name}
            fallbackTitle={false}
            scrim="hero"
            className="aspect-video h-auto max-h-none min-h-0 rounded-lg"
          />
        </Reveal>
      )}

      <div className={`mt-4 flex items-center sm:mt-0 ${HERO_ROW}`}>
        {/* A pointer shortcut to the title — "Open show" is the named,
            focusable link, so this one stays out of the tab order. */}
        <Reveal kind="case" order={1} className={`hidden sm:block ${HERO_CASE}`}>
          <PerspectiveCover variant="rest">
            <Link to={`/show/${id}`} tabIndex={-1} aria-hidden className="block">
              <Poster
                id={id}
                name={name}
                palette={item.title.palette}
                imageUrl={item.title.posterUrl}
                imageFallbackUrl={item.title.posterFallbackUrl}
                sizes={POSTER_SIZES.hero}
                priority={wide}
                layout={false}
                lift={false}
              />
            </Link>
          </PerspectiveCover>
        </Reveal>

        <div className="flex min-w-0 max-w-[32rem] flex-1 flex-col gap-3">
          <Reveal order={4} className="hidden sm:block">
            <p className="text-kicker text-ink-secondary">{t('tonight.upNext')}</p>
          </Reveal>
          <Reveal order={5}>
            <h2 className="line-clamp-2 text-display-sm text-ink sm:text-display">{name}</h2>
          </Reveal>

          <Reveal order={6} className="flex min-h-6 flex-wrap items-baseline gap-x-3 gap-y-1">
            {entry.nextPending ? (
              <Skeleton className="h-4 w-44" />
            ) : next ? (
              <span className="text-headline text-ink">
                {t('upnext.episodeLong', { season: next.season, episode: next.episode })}
              </span>
            ) : (
              <span className="text-headline text-ink">{t('upnext.caughtUp')}</span>
            )}
            {showEpisodeName ? <span className="text-body text-ink-secondary">{episodeName}</span> : null}
            {entry.isNew ? <NewChip tone="accent" /> : null}
          </Reveal>

          <Reveal order={7}>
            <RevealFill order={9} className="flex max-w-sm items-center gap-3">
              <UpNextProgress
                name={name}
                watched={progress.watched}
                total={progress.total}
                percent={entry.percent}
                className="flex-1"
              />
              <span aria-hidden className="text-caption tabular-nums text-ink-secondary">
                {t('upnext.progressOf', { watched: progress.watched, total: progress.total })}
              </span>
            </RevealFill>
          </Reveal>

          <Reveal order={8} className="mt-1 flex flex-wrap items-center gap-2 sm:mt-2">
            <Button
              variant="accent"
              disabled={!next || entry.isPending}
              onClick={() => void entry.markNext()}
              aria-label={next ? t('upnext.continueA11y', { code: entry.code, title: name }) : undefined}
            >
              {entry.isPending ? (
                <Loader2 className="size-5 animate-spin" aria-hidden />
              ) : (
                <Play className="size-5" strokeWidth={1.75} aria-hidden />
              )}
              {t('upnext.continue')}
              {next ? <span className="text-timecode">{entry.compactCode}</span> : null}
            </Button>
            <Button asChild variant="ghost">
              <Link to={`/show/${id}`}>{t('tonight.openShow')}</Link>
            </Button>
          </Reveal>
        </div>
      </div>
    </article>
  );
}

/** The hero's loading shape: the 16:9 band + copy on a phone; the case + copy from `sm`. */
export function NowShowingSkeleton() {
  const { t } = useLocale();
  return (
    <div
      role="status"
      aria-label={t('tonight.loadingUpNext')}
      className={`flex flex-col gap-4 sm:flex-row sm:items-center ${HERO_ROW}`}
    >
      <Skeleton className="aspect-video w-full rounded-lg sm:hidden" />
      <Skeleton className={`hidden aspect-[2/3] rounded-sm sm:block ${HERO_CASE}`} />
      <div className="flex min-w-0 max-w-[32rem] flex-1 flex-col gap-3">
        <Skeleton className="hidden h-4 w-16 sm:block" />
        <Skeleton className="h-8 w-2/3 sm:h-12" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="hidden h-1 w-full max-w-sm sm:block" />
        <Skeleton className="mt-1 h-10 w-36 sm:mt-2" />
      </div>
    </div>
  );
}

/**
 * One case on the "Also in progress" rail (`PerspectiveCover variant="hover"`,
 * so the poster keeps no shared-element morph). The neutral quick mark rides
 * on the case's front — it turns with the cover and stays clickable — shown
 * on hover/focus, always on touch. `index` is its step in the rail's settle.
 */
function InProgressCover({ item, index }: { item: UpNextItem; index: number }) {
  const { t } = useLocale();
  const entry = useUpNextEntry(item);
  const { id, name, next, progress } = entry;
  return (
    <Reveal
      kind="railItem"
      order={index}
      className="group/cover w-[140px] shrink-0 snap-start md:w-[168px] lg:w-[184px]"
    >
      <PerspectiveCover variant="hover">
        <Link to={`/show/${id}`} aria-label={t('common.openTitle', { name })} className="block">
          <Poster
            id={id}
            name={name}
            palette={item.title.palette}
            imageUrl={item.title.posterUrl}
            imageFallbackUrl={item.title.posterFallbackUrl}
            sizes={POSTER_SIZES.case}
            layout={false}
            lift={false}
          />
        </Link>
        {next ? (
          <div className="absolute bottom-2 left-2 z-10 opacity-0 transition-opacity duration-fast focus-within:opacity-100 group-hover/cover:opacity-100 touch:opacity-100">
            <Button
              variant="neutral"
              size="sm"
              disabled={entry.isPending}
              onClick={() => void entry.markNext()}
              aria-label={t('upnext.markWatchedA11y', { code: entry.code, title: name })}
            >
              {entry.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <Check className="size-4" strokeWidth={1.75} aria-hidden />
              )}
              <span className="text-timecode">{entry.compactCode}</span>
            </Button>
          </div>
        ) : null}
      </PerspectiveCover>
      <UpNextProgress
        name={name}
        watched={progress.watched}
        total={progress.total}
        percent={entry.percent}
        className="mt-2"
      />
      <p className="mt-2 truncate text-caption font-semibold text-ink">{name}</p>
      <div className="mt-0.5 flex min-h-5 items-center gap-2 text-caption text-ink-secondary">
        {entry.nextPending ? (
          <Skeleton className="h-3 w-14" />
        ) : next ? (
          <span className="text-timecode">{entry.code}</span>
        ) : (
          <span>{t('upnext.caughtUp')}</span>
        )}
        {entry.isNew ? <NewChip tone="accent" /> : null}
      </div>
    </Reveal>
  );
}

/**
 * The rest of Up next after the hero, as a rail of cases. The caller wraps
 * it in its `RevealGroup`: the heading rises, the cases swing in one by one.
 */
export function AlsoInProgress({ items }: { items: UpNextItem[] }) {
  const { t } = useLocale();
  const heading = t('tonight.alsoInProgress');
  return (
    <section aria-labelledby="tonight-also" className="flex flex-col gap-4">
      <Reveal>
        <h2 id="tonight-also" className="text-section text-ink">
          {heading}
        </h2>
      </Reveal>
      <RailTrack
        label={heading}
        scrollLeftLabel={t('tonight.alsoScrollLeft')}
        scrollRightLabel={t('tonight.alsoScrollRight')}
        // Room inside the scroll box for a turned case's grown top/bottom edge.
        trackClassName="-my-4 py-4"
      >
        {items.map((item, i) => (
          <InProgressCover key={item.title.id} item={item} index={i} />
        ))}
      </RailTrack>
    </section>
  );
}
